import { ApolloError } from "@apollo/client";
import { Save } from "@mui/icons-material";
import { Alert, Button, Stack, TextField, Typography } from "@mui/material";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import { GqlCourseCreateInput, GqlCourseUpdateInput } from "~/__generated__";
import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";

type Props = {
    courseData?: GqlCourseCreateInput | GqlCourseUpdateInput;
    error?: ApolloError;
    onSubmit: SubmitHandler<GqlCourseCreateInput | GqlCourseUpdateInput>;
};

const initialData: GqlCourseCreateInput = {
    name: "",
    description: "",
};

export default function CourseForm({ onSubmit, courseData = initialData, error = undefined }: Props) {
    const { t } = useTranslation("course");
    const {
        handleSubmit,
        register,
        setError,
        formState: { errors, isLoading, isDirty, isSubmitted, isSubmitting },
    } = useForm<GqlCourseCreateInput | GqlCourseUpdateInput>({
        values: courseData,
    });

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);

    useApplyErrorToForm(error, setError);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <GaiaConfirmCloseDialog blocker={blocker} />
            <Stack gap={2}>
                {errors.root && (
                    <Alert severity="error">
                        <Typography>{errors.root.message}</Typography>
                    </Alert>
                )}

                <TextField
                    label={t("courseForm.labelName")}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    {...register("name")}
                />

                <TextField
                    label={t("courseForm.labelDescription")}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    minRows={5}
                    {...register("description")}
                    multiline
                />

                <Button
                    sx={{ mt: 3 }}
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={!isDirty}
                    loading={isLoading}
                >
                    <Save />
                    {t("courseForm.btnSubmit")}
                </Button>
            </Stack>
        </form>
    );
}
